import { Avatar, AvatarFallback } from '@sportik/ui/primitives/avatar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@sportik/ui/primitives/dropdown-menu';
import {
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from '@sportik/ui/primitives/sidebar';
import { ChevronsUpDownIcon, LogOutIcon } from 'lucide-react';
import { auth } from '@/services/auth';

type NavUserProps = {
  user: { name: string; email: string };
};

// "Maria Lopez" → "ML".
function initials(name: string) {
  return name
    .split(/\s+/)
    .slice(0, 2)
    .map((word) => word.charAt(0))
    .join('')
    .toUpperCase();
}

function UserAvatar({ name }: { name: string }) {
  return (
    <Avatar className="rounded-lg">
      <AvatarFallback className="rounded-lg bg-sidebar-primary text-sidebar-primary-foreground">
        {initials(name)}
      </AvatarFallback>
    </Avatar>
  );
}

/** The signed-in account, opening its menu: the stock shadcn `nav-user`. */
export function NavUser({ user }: NavUserProps) {
  const { isMobile } = useSidebar();
  const identity = (
    <>
      <UserAvatar name={user.name} />
      <div className="grid flex-1 text-left text-sm leading-tight">
        <span className="truncate font-medium">{user.name}</span>
        <span className="truncate text-xs">{user.email}</span>
      </div>
    </>
  );

  return (
    <SidebarMenu>
      <SidebarMenuItem>
        <DropdownMenu>
          <DropdownMenuTrigger
            render={
              <SidebarMenuButton size="lg" className="aria-expanded:bg-muted" />
            }
          >
            {identity}
            <ChevronsUpDownIcon className="ml-auto size-4" />
          </DropdownMenuTrigger>
          <DropdownMenuContent
            className="w-fit"
            side={isMobile ? 'bottom' : 'right'}
            align="end"
            sideOffset={4}
          >
            <DropdownMenuGroup>
              <DropdownMenuLabel className="p-0 font-normal">
                <div className="flex items-center gap-2 px-1 py-1.5 text-left text-sm">
                  {identity}
                </div>
              </DropdownMenuLabel>
            </DropdownMenuGroup>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={() => void auth.signOut()}>
              <LogOutIcon />
              Sign out
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </SidebarMenuItem>
    </SidebarMenu>
  );
}
